import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Landmark, Pen, Trash } from "lucide-react";
import { useGetRibs } from "@/api/rib.api";
import { Button } from "@/components/ui/button";
import { OptionsRibUpdateModal } from "./optionsRibUpdateModal";
import { RibDto } from "@/types/api/api-types";
import { OptionsRibCreateModal } from "./optionsRibCreateModal";
import { OptionsRibDeleteModal } from "./optionsRibDeleteModal";

type RibModal =
  | { kind: "create" }
  | { kind: "edit"; rib: RibDto }
  | { kind: "delete"; rib: RibDto }
  | null;

export const OptionsRib = () => {
  const { t } = useTranslation("boParameters");
  const { data: ribs } = useGetRibs();

  const [modal, setModal] = useState<RibModal>(null);
  const closeModal = () => setModal(null);

  const formatRibLabel = (rib: RibDto) =>
    [
      rib.shortenName,
      rib.isDefault && t("ribOption.defaultTag"),
      rib.isPrelevement && t("ribOption.directDebitTag"),
    ]
      .filter(Boolean)
      .join(" - ");

  return (
    <div className="flex w-full max-w-[600px] flex-col gap-2 rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-row justify-between gap-4">
        <div className="flex w-fit flex-row items-center gap-4">
          <Landmark className="text-black" width={24} height={24} />
          <h3 className="text-lg font-semibold text-black">RIB</h3>
        </div>
        <Button type="button" onClick={() => setModal({ kind: "create" })} variant="tertiary">
          {t("ribOption.add")}
        </Button>
      </div>
      {ribs?.length === 0 && <p className="text-sm text-gray-500">{t("ribOption.empty")}</p>}
      {ribs?.map((rib) => (
        <div
          key={rib.id}
          className="flex flex-row items-center justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4"
        >
          <p className="text-xs font-medium text-gray-500">{formatRibLabel(rib)}</p>
          <div className="flex flex-row items-center gap-2">
            <p className="text-sm font-medium text-gray-900">{rib.iban}</p>
            <Button
              type="button"
              onClick={() => setModal({ kind: "edit", rib })}
              variant="ghost"
              className="px-0"
            >
              <Pen className="text-black" width={16} height={16} />
            </Button>
            {!rib.isDefault && (
              <Button
                type="button"
                onClick={() => setModal({ kind: "delete", rib })}
                variant="ghost"
                className="px-0"
              >
                <Trash className="text-black" width={16} height={16} />
              </Button>
            )}
          </div>
        </div>
      ))}
      {modal?.kind === "create" && <OptionsRibCreateModal closeModal={closeModal} />}
      {modal?.kind === "edit" && <OptionsRibUpdateModal closeModal={closeModal} rib={modal.rib} />}
      {modal?.kind === "delete" && (
        <OptionsRibDeleteModal closeModal={closeModal} rib={modal.rib} />
      )}
    </div>
  );
};
